import { Pagination } from '@/constants/pagination';
import { PaginationAPI } from '@/types/pagination';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type OrderState = {
  data: OrderType[];
  myClaims: OrderClaimGroupType[];
  myClaimsPagination: PaginationAPI;
  pagination: PaginationAPI;
  totalElements?: number;
  loading: boolean;
  filters: OrderFilterState;
  orderDetail: OrderType | null;
  filterOptions: any;
};

export const orderInitialState: OrderState = {
  data: [],
  myClaims: [],
  myClaimsPagination: {
    pageNumber: Pagination.pageNumber,
    totalPages: 0,
    totalElements: 0,
    pageSize: Pagination.pageSize,
  },
  pagination: {
    pageNumber: Pagination.pageNumber,
    totalPages: 0,
    totalElements: 0,
    pageSize: Pagination.pageSize,
  },
  loading: false,
  filters: {},
  orderDetail: null,
  filterOptions: null,
};

const slice = createSlice({
  name: 'order',
  initialState: orderInitialState,
  reducers: {
    setListOrderAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: OrderType[];
        pagination?: PaginationAPI;
        totalElements?: number;
        append?: boolean;
      }>,
    ) {
      if (payload.append) {
        state.data = [...state.data, ...payload.data];
      } else {
        state.data = payload.data;
      }

      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },

    setMyClaimsAction(
      state,
      action: PayloadAction<{
        data: OrderClaimGroupType[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      if (action.payload.append) {
        state.myClaims = [...state.myClaims, ...action.payload.data];
      } else {
        state.myClaims = action.payload.data;
      }

      state.myClaimsPagination = action.payload.pagination || state.myClaimsPagination;
    },

    setOrderLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    setOrderFiltersAction(state, action: PayloadAction<OrderFilterState>) {
      state.filters = { ...state.filters, ...action.payload };
    },

    resetOrderFiltersAction(state) {
      state.filters = {};
    },

    setOrderDetailAction(state, action: PayloadAction<OrderType | null>) {
      state.orderDetail = action.payload;
    },

    clearOrderDetailAction(state) {
      state.orderDetail = null;
    },

    setOrderFilterOptionsAction(state, action: PayloadAction<any>) {
      state.filterOptions = action.payload;
    },

    updateOrderItemClaimedAction(state, { payload }: PayloadAction<string>) {
      state.data = state.data.map((order) => ({
        ...order,
        items: order.items?.map((item) =>
          item.id === payload
            ? {
                ...item,
                isClaimed: true,
              }
            : item,
        ),
      }));
    },
  },
});

export const {
  setListOrderAction,
  setMyClaimsAction,
  setOrderLoading,
  setOrderFiltersAction,
  resetOrderFiltersAction,
  setOrderDetailAction,
  clearOrderDetailAction,
  setOrderFilterOptionsAction,
  updateOrderItemClaimedAction,
} = slice.actions;

export default slice.reducer;
